import { CUSTOM_ELEMENTS_SCHEMA, Component, HostListener, computed, inject, input, output, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { selectAllUsers, selectUser } from '../../../../../store/user/user.selectors';
import { CommonModule } from '@angular/common';
import { ChannelCreate, ChannelParticipants } from '../../../../../store/channel/channel.models';
import { ChannelCreateRequest, ChannelCreateSuccess } from '../../../../../store/channel/channel.actions';
import { getAvatarAccent } from '../../home.models';
import { Actions, ofType } from '@ngrx/effects';
import { take } from 'rxjs';
import { WebSocketFacade } from '../../../../../core/facade/websocket.facade';
import { v4 as uuid } from 'uuid';
import 'emoji-picker-element';

// TODO: Replace mock data with a real API call — inject UserService and fetch from /api/users
type SuggestedPerson = {
  name: string;
  email: string;
  initials: string;
  id : string;
};

type SentMessage = {
  text: string;
  time: string;
};

export type NewConversationStartedEvent = {
  channelId: string;
  recipients: SuggestedPerson[];
  message: SentMessage;

};

@Component({
  selector: 'app-chat-new-conversation',
  imports: [ReactiveFormsModule, MatIconModule, CommonModule],
  templateUrl: './chat-new-conversation.component.html',
  styleUrl: './chat-new-conversation.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ChatNewConversationComponent {
  private readonly fb = inject(FormBuilder);
  readonly prefillRecipientUserId = input<string | null>(null);
  // Consumed by Home via (closeComposer) to leave new-chat mode.
  readonly closeComposer = output<void>();
  // Consumed by Home via (conversationStarted) to insert/select the new chat locally.
  readonly conversationStarted = output<NewConversationStartedEvent>();
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly chatSocketFacade = inject(WebSocketFacade);

  protected readonly suggestedPeople: SuggestedPerson[] = [];

  protected readonly form = this.fb.group({
    search: this.fb.nonNullable.control(''),
    message: this.fb.nonNullable.control(''),
  });

  protected get searchControl(): FormControl<string> {
    return this.form.controls.search;
  }

  protected get messageControl(): FormControl<string> {
    return this.form.controls.message;
  }

  private readonly searchValue = toSignal(this.form.controls.search.valueChanges, {
    initialValue: '',
  });

  private readonly currentUser = toSignal(this.store.select(selectUser), { initialValue: null });

  protected readonly selectedRecipients = signal<SuggestedPerson[]>([]);
  protected readonly showSuggestions = signal(true);
  protected readonly sentMessages = signal<SentMessage[]>([]);
  protected readonly isEmojiPickerOpen = signal(false);

  // Filters users for the recipient picker and excludes the logged-in user plus already-selected recipients.
  protected readonly filteredPeople = computed(() => {
    const selected = new Set(this.selectedRecipients().map((p) => p.email));
    const query = this.searchValue().trim().toLowerCase();
    const loggedInUser = this.currentUser();
    const loggedInUserId = loggedInUser?.id;
    const loggedInUserEmail = loggedInUser?.email?.toLowerCase();

    return this.suggestedPeople.filter((person) => {
      if (selected.has(person.email)) return false;
      if (loggedInUserId && person.id === loggedInUserId) return false;
      if (loggedInUserEmail && person.email.toLowerCase() === loggedInUserEmail) return false;
      if (!query) return true;
      return person.name.toLowerCase().includes(query) || person.email.toLowerCase().includes(query);
    });
  });

  ngOnInit(): void {
    // User list is provided by the parent page through the shared store request in Home.ngOnInit.
    this.store.select(selectAllUsers).subscribe((user) => {
        if (user) {
            this.suggestedPeople.length = 0; // Clear existing suggestions
            this.suggestedPeople.push(...user.map(u => ({
                name: u.name,
                email: u.email,
                id: this.resolveUserId(u),
                initials: this.createInitials(u.name)
            })));
            this.applyPrefillRecipient();
        }
    });

  }

  private resolveUserId(user: unknown): string {
    const candidate = user as { id?: string; _id?: string; user_id?: string; userId?: string };
    return candidate.id || candidate._id || candidate.user_id || candidate.userId || '';
  }

  protected createInitials(name: string): string {
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  protected getPersonAccent(person: SuggestedPerson): string {
    return getAvatarAccent(person.id || person.email || person.name);
  }

  // Called from recipient chip clicks in the new-conversation template.
  protected onSelectPerson(person: SuggestedPerson): void {
    this.selectedRecipients.update((list) => [...list, person]);
    this.form.controls.search.reset();
  }

  // Called from each recipient chip remove button.
  protected onRemoveRecipient(email: string): void {
    this.selectedRecipients.update((list) => list.filter((p) => p.email !== email));
  }

  protected toggleSuggestions(): void {
    this.showSuggestions.update((v) => !v);
  }

  protected onClose(): void {
    this.resetComposerState();
    this.closeComposer.emit();
  }

  protected toggleEmojiPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.isEmojiPickerOpen.update((isOpen) => !isOpen);
  }

  protected onEmojiPickerClick(event: Event): void {
    event.stopPropagation();
  }

  protected onEmojiPicked(event: Event): void {
    const emoji = (event as CustomEvent<{ unicode?: string }>).detail?.unicode;
    if (!emoji) {
      return;
    }

    const message = this.messageControl.value ?? '';
    this.messageControl.setValue(`${message}${emoji}`);
    this.isEmojiPickerOpen.set(false);
  }

  // Creates the channel first, then emits back to Home so the new chat can be selected in the main layout.
  protected onSendMessage(): void {
    const message = this.messageControl.value.trim();
    const recipients = [...this.selectedRecipients()];
    if (!message || recipients.length === 0) return;  

    var finalParticipantList: ChannelParticipants[] = recipients
      .map(r => ({
      user_id: r.id,
      name: r.name,
      is_active_on_channel: true,
      has_left_channel: false,
      last_read_message_id: null,
      last_read_at: null,
      unread_count: 0,
    }))
      .filter((participant) => Boolean(participant.user_id));

    let currentUser = this.currentUser();

    if (currentUser) {
      const currentUserAsPerson: ChannelParticipants = {
        user_id: currentUser.id,
        name: currentUser.name,
        is_active_on_channel: true,
        has_left_channel: false,
        last_read_message_id: null,
        last_read_at: null,
        unread_count: 0,

      };
      finalParticipantList.push(currentUserAsPerson);
    }

    finalParticipantList = finalParticipantList.filter(
      (participant, index, arr) => arr.findIndex((p) => p.user_id === participant.user_id) === index,
    );

    if (finalParticipantList.length === 0) {
      console.warn('Channel participants is empty after ID normalization. Verify user id/_id mapping from users API.');
      return;
    }
    
    // create a channel friendly name based on the participant names similar to teanms
    let channelFriendlyName: string = finalParticipantList.length > 2 ? recipients.map(r => r.name).join(', ') : recipients[0].name;
    
    const channel:ChannelCreate = {
      channel_friendly_name: channelFriendlyName, 
      channel_participants: finalParticipantList,

    };

    this.store.dispatch(ChannelCreateRequest({ payload: channel }));

    this.actions$.pipe(ofType(ChannelCreateSuccess), take(1)).subscribe(({ channel }) => {
      const channelId = channel.channel_id;
        const sentMessage = { text: message, time: this.getCurrentTimeLabel() };
      this.chatSocketFacade.subscribe(channelId);
        

        setTimeout(() => {
          this.chatSocketFacade.sendMessage(
            channelId,
            uuid(),
            message,
            'text',
          );
        }, 2000);

        this.sentMessages.update((msgs) => [
        ...msgs,
        sentMessage,
        ]);

        this.conversationStarted.emit({
        channelId,
        recipients,
        message: sentMessage,
    });
    });

    

    this.resetComposerState();
  }

  private applyPrefillRecipient(): void {
    const userId = this.prefillRecipientUserId()?.trim();
    if (!userId) {
      return;
    }

    const person = this.suggestedPeople.find((candidate) => candidate.id === userId);
    if (!person) {
      return;
    }

    const alreadySelected = this.selectedRecipients().some((candidate) => candidate.id === userId);
    if (alreadySelected) {
      return;
    }

    this.selectedRecipients.update((list) => [...list, person]);
    this.form.controls.search.reset();
  }

  // Resets temporary recipient and draft state when the panel closes or completes.
  private resetComposerState(): void {
    this.selectedRecipients.set([]);
    this.sentMessages.set([]);
    this.showSuggestions.set(true);
    this.isEmojiPickerOpen.set(false);
    this.form.controls.search.reset();
    this.form.controls.message.reset();
  }

  private getCurrentTimeLabel(): string {
    return new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.getUserTimeZone(),
    });
  }

  private getUserTimeZone(): string | undefined {
    const timezone = this.currentUser()?.timezone?.trim();
    if (!timezone) {
      return undefined;
    }

    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone }).format(new Date());
      return timezone;
    } catch {
      return undefined;
    }
  }

  @HostListener('document:keydown.escape')
  protected closeOnEscape(): void {
    if (this.isEmojiPickerOpen()) {
      this.isEmojiPickerOpen.set(false);
      return;
    }

    this.resetComposerState();
    this.closeComposer.emit();
  }

  @HostListener('document:click')
  protected closeEmojiPicker(): void {
    if (!this.isEmojiPickerOpen()) {
      return;
    }

    this.isEmojiPickerOpen.set(false);
  }
}
